import { useState } from "react";
import { BellRing, X, Siren, Settings2 } from "lucide-react";
import { AlertRoutingDialog } from "@/components/AlertRouting";
import { RISK } from "@/lib/constants";

export default function AlertConsole({ alerts, onClear, webhookConfigured, onRoutingChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="alert-console" className="glass rounded-lg flex flex-col overflow-hidden shrink-0 max-h-[34%] min-h-[150px]">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Siren className={`w-4 h-4 ${alerts.length ? "text-risk-critical" : "text-slate-500"}`} />
          <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">
            Alert Console
          </h2>
          <span className="font-mono-t text-xs font-bold text-risk-critical">{alerts.length}</span>
        </div>
        {alerts.length > 0 && (
          <button
            data-testid="clear-alerts-button"
            onClick={onClear}
            className="font-mono-t text-[10px] uppercase tracking-wider text-slate-400 hover:text-cyan flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      <div className="px-3 py-1.5 border-b border-border font-mono-t text-[9px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${webhookConfigured ? "bg-risk-low" : "bg-slate-600"}`} />
        <span data-testid="webhook-status">Slack routing {webhookConfigured ? "armed" : "not configured"} · red + amber</span>
        <button data-testid="alert-routing-button" onClick={() => setOpen(true)}
          className="ml-auto flex items-center gap-1 text-slate-400 hover:text-cyan transition-colors">
          <Settings2 className="w-3 h-3" /> Routing
        </button>
      </div>
      <AlertRoutingDialog open={open} onOpenChange={setOpen} onChange={onRoutingChange} />

      <div className="flex-1 overflow-y-auto p-2 space-y-1.5 max-h-[220px]">
        {alerts.length === 0 && (
          <div className="text-center text-slate-600 font-mono-t text-xs py-6 flex flex-col items-center gap-2">
            <BellRing className="w-5 h-5" /> No active threshold breaches<span className="text-[9px] text-slate-400 normal-case">Alerts track live conditions only — drills and replays do not raise alerts</span>
          </div>
        )}
        {alerts.map((a) => {
          const rc = RISK[a.tier] || RISK.critical;
          return (
            <div
              key={a.id}
              data-testid={`alert-${a.id}`}
              className="rounded-md p-2 fade-up border"
              style={{ background: `${rc.color}14`, borderColor: `${rc.color}66` }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-heading font-semibold text-xs text-slate-100 truncate">{a.name}</div>
                <span className="font-mono-t text-[10px] font-bold px-1.5 rounded shrink-0"
                      style={{ color: rc.color, background: `${rc.color}1a` }}>
                  {a.freeboard_m != null ? `${a.freeboard_m} m` : ""}
                </span>
              </div>
              <div className="font-mono-t text-[9px] text-slate-400 uppercase tracking-wide mt-0.5">
                {a.tier === "high" ? "AMBER · 72 h forecast" : "RED · now"} · {a.asset_type} · {a.scenario}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
